import { useCallback, useEffect, useRef, useState } from 'react'
import { CowSwapWidget, TradeType } from '@cowprotocol/widget-react'
import { CowWidgetEvents } from '@cowprotocol/events'
import { T } from '@/lib/theme'
import { Badge } from '@/components/ui'
import { SectionHeader } from '@/components/dashboard/SectionHeader'
import { ADDRESSES, CHAIN_ID, ZERO_ADDR } from '@/lib/contracts'
import { useProtocolState } from '@/hooks/useProtocolState'
import { useEip1193Provider } from '@/hooks/useEip1193Provider'


// DexScreener's own recognized chain slugs - same set StakeSection's own UNISWAP_CHAIN_SLUG
// already covers for the Buy HORIZON link, since DexScreener indexes the same major chains
// Uniswap's own web app serves.
const DEXSCREENER_CHAIN_SLUG = { 1: 'ethereum', 8453: 'base', 42161: 'arbitrum', 10: 'optimism', 137: 'polygon' }

// CoW Protocol's own real supported-chain list (narrower than DexScreener's/Uniswap's - notably
// no Optimism) - checked directly against @cowprotocol/widget-lib's own SupportedChainId, not
// assumed to match the other two maps above.
const COWSWAP_CHAIN_IDS = new Set([1, 100, 42161, 8453, 137, 43114, 56, 59144, 9745, 57073, 11155111])

// Our own cut of every swap routed through the widget - CoW's own hard cap is 100 bps (1%),
// see PartnerFee in @cowprotocol/widget-lib. Paid straight to whatever `treasury` currently is
// (read live off-chain, same as every other treasury reference in this app - genesis can move
// it, so this never hardcodes an address), same "protocol revenue to treasury" convention every
// other fee stream in this app already follows.
const PARTNER_FEE_BPS = 30

// Real, extremely deep Uniswap V3 pool (Ethereum mainnet, 0.05% tier, $100M+ TVL, verified
// directly rather than guessed) - the fallback chart/swap preview whenever the current chain
// isn't one DexScreener/CoW Swap actually cover (local dev, or a chain CoW's narrower list
// excludes), so the panel always shows a real, functioning widget instead of a dead placeholder.
// Clearly labeled as a preview, not presented as HORIZON's own data.
const FALLBACK_DEXSCREENER = { chain: 'ethereum', pool: '0x88e6a0c2ddd26feeb64f039a2c41296fcb3f5640' }
const FALLBACK_COWSWAP = { chainId: 1, sell: 'WETH', buy: 'USDC' }

/// Swap + chart, in one place, both real embedded widgets rather than anything hand-rolled -
/// CoW Swap already routes through Uniswap V3 (this pool's own venue) among everything else its
/// solvers cover, and DexScreener already indexes this pool's real trade history the moment it
/// has any, so there's no separate liquidity/routing logic for this app to own. Swap sits above
/// the chart - it's the action this page exists for, the chart is context underneath it, not the
/// other way around. Neither widget can show real HORIZON data on a chain it doesn't cover (local
/// dev, or - for CoW - a chain outside its own narrower supported list), so each panel falls back
/// to a real, well-known pair on Ethereum mainnet instead of a dead placeholder - always a
/// working widget, clearly labeled as a preview when it isn't actually showing HORIZON.
export function BuySection() {
  const { d } = useProtocolState()
  // The HORIZON/USDx pool, straight from ADDRESSES rather than resolved through a contract that
  // holds a position in it - see useHorizonPriceUsd for the same reasoning.
  const poolAddress = ADDRESSES.HORIZON_WETH_POOL

  // Tracks whatever non-USDx token the swap widget currently has selected, so the chart below
  // can follow it - null means "show the HORIZON/USDx pool" (the default). Lives here, not in
  // either panel, since it's produced by CowSwapPanel and consumed by DexScreenerPanel.
  const [chartToken, setChartToken] = useState(null) // { chainId, address } | null

  return (
    <div id="buy">
      <SectionHeader
        title="Buy"
        subtitle="Swap for HORIZON, with a live chart that follows whatever you select."
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
        <CowSwapPanel treasury={d.treasury} onTokenChange={setChartToken} />
        <DexScreenerPanel poolAddress={poolAddress} chartToken={chartToken} />
      </div>
    </div>
  )
}

function CowSwapPanel({ treasury, onTokenChange }) {
  // Reuses whatever wallet is already connected via the nav's own RainbowKit button, instead of
  // making the widget run its own independent connect flow inside its iframe - on mobile
  // especially, having to connect twice (once in the nav, once again inside the widget) is jarring.
  const provider = useEip1193Provider()
  const supported = COWSWAP_CHAIN_IDS.has(CHAIN_ID)
  // Default the swap to ETH -> HORIZON: native ETH is what most first-time buyers arrive holding,
  // so it's the friendlier starting pair than USDx. CoW takes the 'ETH' symbol for the chain's
  // native currency (same style as the fallback pair below, which uses 'WETH'/'USDC' symbols).
  // Left as a plain default - the widget's own selector still lets them switch to USDx or anything
  // else, and the chart below follows whatever they pick.
  const sellToken = 'ETH'
  const buyToken = ADDRESSES.HORIZON_TOKEN
  const isHorizon = supported && !!sellToken && !!buyToken && sellToken !== ZERO_ADDR && buyToken !== ZERO_ADDR
  const hasTreasury = !!treasury && treasury !== ZERO_ADDR

  const chainId = isHorizon ? CHAIN_ID : FALLBACK_COWSWAP.chainId
  const sell = isHorizon ? sellToken : FALLBACK_COWSWAP.sell
  const buy  = isHorizon ? buyToken : FALLBACK_COWSWAP.buy

  // Debounced so a burst of ON_CHANGE_TRADE_PARAMS events (unclear from CoW's own docs whether
  // this fires per-keystroke while typing an amount or only on settled changes) doesn't reload
  // the DexScreener iframe below on every event - only the token that's still selected 400ms
  // after the last change actually gets charted.
  const debounceRef = useRef(null)
  const handleTradeParamsChange = useCallback((payload) => {
    if (!onTokenChange) return
    const usdxAddr = (ADDRESSES.USDX || '').toLowerCase()
    // Whichever side of the trade ISN'T USDx is the one worth charting - usually buyToken (the
    // thing being acquired), but fall back to sellToken so a HORIZON->somethingElse trade still
    // tracks correctly.
    const candidate = [payload.buyToken, payload.sellToken]
      .find((t) => t?.address && t.address.toLowerCase() !== usdxAddr)
    if (!candidate || !DEXSCREENER_CHAIN_SLUG[candidate.chainId]) return
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      onTokenChange({ chainId: candidate.chainId, address: candidate.address })
    }, 400)
  }, [onTokenChange])

  const params = {
    appCode: 'Horizon',
    chainId,
    tradeType: TradeType.SWAP,
    // Dapp mode: the widget defers to OUR wallet connection (passed via the `provider` prop
    // below) instead of showing its own separate connect button and running an independent
    // connect flow inside its iframe.
    standaloneMode: false,
    // Swap/Limit/Advanced all left available (no enabledTradeTypes restriction) - Advanced
    // (TWAP) only works for Safe wallets and shows CoW's own "Unsupported wallet detected"
    // message for a plain EOA, but that's CoW's real, inherent limitation, the same thing
    // swap.cow.fi itself does - not something to hide the option over.
    sell: { asset: sell },
    buy: { asset: buy },
    theme: 'dark',
    // Fixed pixel heights clip taller forms (TWAP's part/duration fields push past a plain
    // 820px) into an internal scrollbar. var(--dynamicHeight) is CoW's own documented escape
    // hatch: the widget resizes its outer container to whatever its content actually needs,
    // and disableScrollbars is the paired option since nothing constrains the height anymore.
    rootStyle: { width: '100%', height: 'var(--dynamicHeight)' },
    disableScrollbars: true,
    // Only charge the fee once we're actually trading HORIZON on a real supported chain, with a
    // real treasury address to send it to - never on the fallback preview pair, and never to a
    // zero address.
    ...(isHorizon && hasTreasury ? { partnerFee: { bps: PARTNER_FEE_BPS, recipient: treasury } } : {}),
  }

  // Lets the DexScreener chart below follow whatever token the widget's own selector is currently
  // set to, instead of always showing the HORIZON/USDx pool.
  //
  // This MUST be its own prop rather than a field on `params`. `params` is structured-cloned and
  // postMessage'd into the widget's iframe, and a function is not cloneable - putting the listener
  // there threw `DataCloneError: Failed to execute 'postMessage'` on every /buy load, which took
  // the whole page down (the widget never mounted, so #root stayed empty). `listeners` is a sibling
  // prop of `params` in CowSwapWidgetProps precisely because it stays on this side of the boundary:
  // the widget wires it to a message listener locally and never serializes it.
  const listeners = [{ event: CowWidgetEvents.ON_CHANGE_TRADE_PARAMS, handler: handleTradeParamsChange }]

  return (
    <div>
      {/* When the widget is on the fallback pair (a chain CoW doesn't support, e.g. local dev), say
          so plainly - otherwise a WETH->USDC swap card reads as if it were the HORIZON swap. */}
      {!isHorizon && (
        <PreviewNote>HORIZON swaps arrive on this network soon. For now this {FALLBACK_COWSWAP.sell}/{FALLBACK_COWSWAP.buy} widget stays live so the page works.</PreviewNote>
      )}
      <CowSwapWidget params={params} provider={provider} listeners={listeners} />
    </div>
  )
}

/// The "this isn't real HORIZON data" banner, shared by both panels. A small amber-toned badge plus
/// one line - loud enough not to be missed, quiet enough not to dominate a working widget.
function PreviewNote({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
      <Badge color={T.warning}>Preview</Badge>
      <span style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.5 }}>{children}</span>
    </div>
  )
}

/// `chartToken`: set by CowSwapPanel's own ON_CHANGE_TRADE_PARAMS listener whenever the widget's
/// selection changes to something other than HORIZON/USDx - overrides the default farm-pool chart
/// with whatever the user is actually looking to trade. DexScreener's embed URL accepts a plain
/// token address here just as well as a pair address (it resolves to that token's own
/// most-liquid pair automatically), so no extra pool lookup is needed for the override.
function DexScreenerPanel({ poolAddress, chartToken }) {
  const slug = DEXSCREENER_CHAIN_SLUG[CHAIN_ID]
  const hasPool = !!poolAddress && poolAddress !== ZERO_ADDR
  const isHorizon = !!slug && hasPool

  const override = chartToken ? { chain: DEXSCREENER_CHAIN_SLUG[chartToken.chainId], pool: chartToken.address } : null
  const chain = override?.chain ?? (isHorizon ? slug : FALLBACK_DEXSCREENER.chain)
  const pool  = override?.pool ?? (isHorizon ? poolAddress : FALLBACK_DEXSCREENER.pool)
  const src = `https://dexscreener.com/${chain}/${pool}?embed=1&theme=dark&info=0`

  // The chart is fallback data ONLY when neither a real HORIZON pool nor a user-selected token is in
  // play. An override is the token the user themselves picked in the swap - real, not a stand-in -
  // so it carries no preview badge.
  const isPreview = !override && !isHorizon

  // Reset the loading overlay whenever the embedded pool changes (a token switch reloads the iframe
  // to a new src), so the overlay reappears for the new load instead of staying cleared from the
  // previous one.
  const [loaded, setLoaded] = useState(false)
  useEffect(() => { setLoaded(false) }, [src])

  return (
    <div>
      {isPreview && (
        <PreviewNote>HORIZON has no indexed trades on this network yet. This is a live {FALLBACK_DEXSCREENER.chain} WETH/USDC chart, shown as a stand-in.</PreviewNote>
      )}
      {/* Responsive height: 800px was a fixed slab that swallowed the fold on a laptop. min(800px,
          55vh) keeps the tall look on a big monitor and shrinks it on shorter viewports. */}
      <div style={{ position: 'relative', height: 'min(800px, 55vh)', minHeight: 360 }}>
        {!loaded && (
          <div style={{
            position: 'absolute', inset: 0, borderRadius: T.r.lg,
            border: `1px solid ${T.border}`, background: 'rgba(255,255,255,0.02)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption,
          }}>
            Loading chart…
          </div>
        )}
        <iframe
          key={src}
          title={override ? 'Selected token chart' : isHorizon ? 'HORIZON/USDx chart' : 'WETH/USDC chart preview'}
          src={src}
          onLoad={() => setLoaded(true)}
          style={{
            width: '100%', height: '100%', border: 'none', display: 'block', borderRadius: T.r.lg,
            opacity: loaded ? 1 : 0, transition: 'opacity 0.25s ease',
          }}
        />
      </div>
    </div>
  )
}
